import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { PALETTE } from './palette';

/** Asset low-poly dekoratif: semua geometri non-indexed + vertex colors
 *  (flat shading). Dibangun sekali, di-clone + di-applyMatrix per instans,
 *  lalu seluruhnya digabung jadi 1-2 mesh per stage (performa ringan). */

export type PrefabKind = 'treeA' | 'treeB' | 'treeC' | 'grassA' | 'grassB' | 'rock' | 'flower';

interface Parts {
  g: THREE.BufferGeometry;
  c: number;
}

const tmpColor = new THREE.Color();

function colored(geo: THREE.BufferGeometry, hex: number): THREE.BufferGeometry {
  const g = geo.index === null ? geo : geo.toNonIndexed();
  const pos = g.attributes.position as THREE.BufferAttribute;
  const n = pos.count;
  tmpColor.setHex(hex);
  const arr = new Float32Array(n * 3);
  for (let i = 0; i < n; i++) {
    arr[i * 3] = tmpColor.r;
    arr[i * 3 + 1] = tmpColor.g;
    arr[i * 3 + 2] = tmpColor.b;
  }
  g.setAttribute('color', new THREE.BufferAttribute(arr, 3));
  return g;
}

/** Warna lerap y teratas(yHigh) -> y terbawah(yLow) (untuk blade rumput). */
function coloredY(geo: THREE.BufferGeometry, hexLow: number, hexHigh: number): THREE.BufferGeometry {
  const g = geo.index === null ? geo : geo.toNonIndexed();
  const pos = g.attributes.position as THREE.BufferAttribute;
  const n = pos.count;
  const cLow = tmpColor.setHex(hexLow);
  const cHigh = new THREE.Color(hexHigh);
  const arr = new Float32Array(n * 3);
  let minY = Infinity;
  let maxY = -Infinity;
  for (let i = 0; i < n; i++) {
    const y = pos.getY(i);
    if (y < minY) minY = y;
    if (y > maxY) maxY = y;
  }
  const span = Math.max(1e-5, maxY - minY);
  for (let i = 0; i < n; i++) {
    const t = (pos.getY(i) - minY) / span;
    const c = tmpColor.copy(cLow).lerp(cHigh, t);
    arr[i * 3] = c.r;
    arr[i * 3 + 1] = c.g;
    arr[i * 3 + 2] = c.b;
  }
  g.setAttribute('color', new THREE.BufferAttribute(arr, 3));
  return g;
}

function mergeParts(parts: Parts[]): THREE.BufferGeometry {
  const geos: THREE.BufferGeometry[] = [];
  for (const p of parts) geos.push(colored(p.g, p.c));
  const merged = mergeGeometries(geos, false);
  if (!merged) throw new Error('mergeGeometries gagal membangun prefab dekorasi');
  merged.computeVertexNormals();
  return merged;
}

function rimCyl(r0: number, r1: number, h: number, seg: number): THREE.CylinderGeometry {
  return new THREE.CylinderGeometry(r1, r0, h, seg);
}

/** Pohon A: batang krem + dua kerucut bertumpuk (mint/sage). Tinggi ~2.0. */
function buildTreeA(): THREE.BufferGeometry {
  return mergeParts([
    { g: rimCyl(0.14, 0.18, 0.6, 5), c: PALETTE.trunkCream },
    { g: new THREE.ConeGeometry(0.8, 0.95, 7), c: PALETTE.canopyMint },
    { g: new THREE.ConeGeometry(0.52, 0.7, 6), c: PALETTE.canopySage },
  ]);
}

/** Pohon B: batang pendek + kanopi bola faset (icosahedron detail 0). */
function buildTreeB(): THREE.BufferGeometry {
  const canopy = new THREE.IcosahedronGeometry(0.72, 0);
  canopy.scale(1, 0.92, 1);
  canopy.translate(0, 1.0, 0);
  const trunk = rimCyl(0.16, 0.2, 0.45, 5);
  trunk.translate(0, 0.225, 0);
  return mergeParts([
    { g: trunk, c: PALETTE.trunkDeep },
    { g: canopy, c: PALETTE.canopyTosca },
  ]);
}

/** Pohon C: pinus ramping (3 kerucut menyusut), sage. Tinggi ~2.2. */
function buildTreeC(): THREE.BufferGeometry {
  const cone1 = new THREE.ConeGeometry(0.44, 0.9, 6);
  cone1.translate(0, 0.85, 0);
  const cone2 = new THREE.ConeGeometry(0.32, 0.62, 5);
  cone2.translate(0, 1.38, 0);
  const cone3 = new THREE.ConeGeometry(0.2, 0.48, 4);
  cone3.translate(0, 1.78, 0);
  const trunk = rimCyl(0.08, 0.1, 0.5, 5);
  trunk.translate(0, 0.25, 0);
  return mergeParts([
    { g: trunk, c: PALETTE.trunkDeep },
    { g: cone1, c: PALETTE.canopySage },
    { g: cone2, c: PALETTE.canopyMint },
    { g: cone3, c: PALETTE.canopySage },
  ]);
}

/** Satu blade rumput: kerucut runcing 4 segmen, condong acak, gradient mint->sage. */
function blade(len: number, lean: number, tilt: number): THREE.BufferGeometry {
  const g = new THREE.ConeGeometry(0.055, len, 4);
  g.rotateZ(lean);
  g.rotateX(tilt);
  g.translate(0, len * 0.5, 0);
  return coloredY(g, PALETTE.grassMint, PALETTE.canopySage);
}

/** Rumpun rumput: N blade tersebar lingkaran kecil. */
function buildGrass(count: number, r0: number, r1: number, lenMin: number, lenMax: number): THREE.BufferGeometry {
  const parts: THREE.BufferGeometry[] = [];
  for (let i = 0; i < count; i++) {
    const t = i / Math.max(1, count - 1);
    const ang = t * Math.PI * 2 + (i % 2) * 0.7;
    const rad = r0 + (r1 - r0) * ((i * 37) % 10) / 10;
    const x = Math.cos(ang) * rad;
    const z = Math.sin(ang) * rad;
    const len = lenMin + (lenMax - lenMin) * ((i * 13) % 10) / 10;
    const lean = 0.08 + 0.14 * ((i * 7) % 10) / 10;
    const b = blade(len, lean, ((i * 29) % 10) / 10 - 0.5);
    b.translate(x, 0, z);
    parts.push(b);
  }
  const merged = mergeGeometries(parts, false);
  if (!merged) throw new Error('gagal merge rumput');
  merged.computeVertexNormals();
  return merged;
}

/** Batu bulat pastel (icosahedron faset, squash). */
function buildRock(): THREE.BufferGeometry {
  const g = new THREE.IcosahedronGeometry(0.42, 0);
  g.scale(1, 0.55, 0.9);
  g.rotateY(0.6);
  return mergeParts([{ g, c: PALETTE.rockLavender }]);
}

/** Bunga: tangkai sage + 5 kelopak pastel + pusat kuning. */
function buildFlower(): THREE.BufferGeometry {
  const stem = rimCyl(0.02, 0.025, 0.3, 4);
  stem.translate(0, 0.15, 0);
  const parts: Parts[] = [
    { g: stem, c: PALETTE.grassSage },
  ];
  const petalGeo = new THREE.SphereGeometry(0.05, 5, 3);
  petalGeo.scale(1.05, 0.5, 1.7);
  const petalColors = [PALETTE.petalPink, PALETTE.petalYellow, PALETTE.petalLilac, PALETTE.petalWhite, PALETTE.petalPink];
  for (let i = 0; i < 5; i++) {
    const p = new THREE.ConeGeometry(0.045, 0.12, 5);
    p.rotateX(-Math.PI / 2);
    p.translate(Math.cos((i / 5) * Math.PI * 2 + 0.4) * 0.07, 0.34, Math.sin((i / 5) * Math.PI * 2 + 0.4) * 0.07);
    parts.push({ g: p, c: petalColors[i % petalColors.length] });
  }
  parts.push({ g: new THREE.SphereGeometry(0.045, 6, 4), c: PALETTE.center });
  return mergeParts(parts);
}

function buildAll(): Record<PrefabKind, THREE.BufferGeometry> {
  return {
    treeA: buildTreeA(),
    treeB: buildTreeB(),
    treeC: buildTreeC(),
    grassA: buildGrass(3, 0.02, 0.18, 0.24, 0.42),
    grassB: buildGrass(5, 0.02, 0.22, 0.18, 0.34),
    rock: buildRock(),
    flower: buildFlower(),
  };
}

export const PREFABS: Record<PrefabKind, THREE.BufferGeometry> = buildAll();

export interface DecoInstance {
  kind: PrefabKind;
  x: number;
  z: number;
  /** Skala seragam (mengatur kedalaman: belakang > depan, atau variasi). */
  s?: number;
  /** Rotasi yaw (rad). */
  a?: number;
}

/** Dekorasi cast shadow (pohon/batu/bunga) vs murni visual (rumput). */
export const DECO_SHADOW: Record<PrefabKind, boolean> = {
  treeA: true,
  treeB: true,
  treeC: true,
  grassA: false,
  grassB: false,
  rock: true,
  flower: true,
};

export interface Placement {
  geo: THREE.BufferGeometry;
  x: number;
  y: number;
  z: number;
  a: number;
  s: number;
}

export function place(geo: THREE.BufferGeometry, x: number, y: number, z: number, a = 0, s = 1): Placement {
  return { geo, x, y, z, a, s };
}

/** Gabungkan instance jadi SATU BufferGeometry ber-vertex-color wereld,
 *  siap render 1 draw call (performa: dekorasi statis tak perlu collider
 *  detail; rumput tanpa cast shadow). */
export function mergeInstances(list: Placement[]): THREE.BufferGeometry {
  const geos: THREE.BufferGeometry[] = [];
  const m = new THREE.Matrix4();
  const q = new THREE.Quaternion();
  const e = new THREE.Euler();
  const p = new THREE.Vector3();
  const s = new THREE.Vector3();
  for (const it of list) {
    const g = it.geo.clone();
    p.set(it.x, it.y, it.z);
    e.set(0, it.a, 0);
    q.setFromEuler(e);
    s.set(it.s, it.s, it.s);
    m.compose(p, q, s);
    g.applyMatrix4(m);
    geos.push(g);
  }
  const merged = mergeGeometries(geos, false);
  if (!merged) throw new Error('gagal merge dekorasi');
  merged.computeVertexNormals();
  return merged;
}

/** Kotak eksklusi gameplay -> zona aman dekorasi. */
export function expandedPlayBounds(
  list: { minX: number; maxX: number; minZ: number; maxZ: number }[],
  pad: number,
): { minX: number; maxX: number; minZ: number; maxZ: number } {
  let minX = Infinity;
  let maxX = -Infinity;
  let minZ = Infinity;
  let maxZ = -Infinity;
  for (const b of list) {
    if (b.minX < minX) minX = b.minX;
    if (b.maxX > maxX) maxX = b.maxX;
    if (b.minZ < minZ) minZ = b.minZ;
    if (b.maxZ > maxZ) maxZ = b.maxZ;
  }
  return { minX: minX - pad, maxX: maxX + pad, minZ: minZ - pad, maxZ: maxZ + pad };
}

/** PRNG deterministik (mulberry32): hasil scatter stabil antar build, bisa
 *  diuji unit. */
export function seededRng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Sebar N rumput di zona aman (luar kotak eksklusi), deterministik. */
export function scatterGrass(
  arenaHalf: number,
  safe: { minX: number; maxX: number; minZ: number; maxZ: number },
  count: number,
  seed: number,
): DecoInstance[] {
  const rnd = seededRng(seed);
  const margin = 1.4;
  const out: DecoInstance[] = [];
  const minX = -arenaHalf + margin;
  const maxX = arenaHalf - margin;
  const minZ = -arenaHalf + margin;
  const maxZ = arenaHalf - margin;
  let guard = 0;
  while (out.length < count && guard < count * 40) {
    guard++;
    const x = minX + rnd() * (maxX - minX);
    const z = minZ + rnd() * (maxZ - minZ);
    if (x > safe.minX && x < safe.maxX && z > safe.minZ && z < safe.maxZ) continue;
    out.push({
      kind: rnd() < 0.45 ? 'grassA' : 'grassB',
      x,
      z,
      s: 0.75 + rnd() * 0.6,
      a: rnd() * Math.PI * 2,
    });
  }
  return out;
}